import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clinicBankTransactionApi,
  clinicBankTransactionQueryKeys,
  type UnmatchedTransaction,
} from "../../shared/api/clinicBankAccount";
import { apiError } from "../../shared/api/client";
import { formatVndWithUnit } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/**
 * Tiền đã về tài khoản nhưng chưa gắn được vào hoá đơn nào (#18998).
 *
 * <p>Màn hình này là nơi tự động hoá GIAO LẠI việc cho con người. Nó không có mặt vì hệ thống làm
 * chưa tốt — nó có mặt vì có những khoản mà đoán là sai: chuyển thiếu, chuyển thừa, mất mã, một lần
 * chuyển cho hai hoá đơn, hoặc tiền chẳng liên quan gì tới viện phí.
 */
export function UnmatchedTransfersPage() {
  const qc = useQueryClient();
  const rows = useQuery({
    queryKey: clinicBankTransactionQueryKeys.unmatched(),
    queryFn: clinicBankTransactionApi.unmatched,
  });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Tiền về chưa khớp hoá đơn"
        description="Tiền đã nằm trong tài khoản phòng khám. Hệ thống không tự đoán khoản nào của hoá đơn nào — chỗ này để người xử lý."
      />
      {rows.isPending && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {rows.data?.length === 0 && (
        <Card>
          <CardContent className="p-5 text-sm text-muted-foreground">
            Không có khoản nào chờ xử lý.
          </CardContent>
        </Card>
      )}
      <div className="space-y-3">
        {(rows.data ?? []).map((row) => (
          <UnmatchedRow
            key={row.id}
            row={row}
            onResolved={() =>
              qc.invalidateQueries({ queryKey: clinicBankTransactionQueryKeys.unmatched() })
            }
          />
        ))}
      </div>
    </div>
  );
}

function UnmatchedRow({
  row,
  onResolved,
}: {
  row: UnmatchedTransaction;
  onResolved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: () => clinicBankTransactionApi.resolve(row.id, note.trim()),
    onSuccess: onResolved,
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <strong className="text-lg tabular-nums">{formatVndWithUnit(row.amountVnd)}</strong>
          <span className="text-xs text-muted-foreground tabular-nums">
            {new Date(row.occurredAt).toLocaleString("vi-VN")}
          </span>
        </div>
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
          <Field label="Vì sao chưa khớp" value={row.reason} strong />
          <Field label="Tài khoản nhận" value={`${row.gateway} · ${row.accountNo}`} />
          <Field label="Nội dung chuyển khoản" value={row.transferContent} />
          <Field label="Mã giao dịch" value={`${row.provider} · ${row.providerTxnId}`} />
        </dl>
        <div className="space-y-1.5">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Đã xử lý thế nào? VD: đã chuyển trả người gửi 16/09, hoặc đã thu vào HD-20260916-002"
          />
          <p className="text-xs text-muted-foreground">
            Bắt buộc ghi rõ. Không có dòng này thì khoản tiền hiện lại ở mọi lần đối soát sau.
          </p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button
          size="sm"
          variant="outline"
          disabled={!note.trim() || resolve.isPending}
          onClick={() => resolve.mutate()}
        >
          {resolve.isPending ? "Đang lưu…" : "Đánh dấu đã xử lý"}
        </Button>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  strong,
}: {
  label: string;
  value: string | null;
  strong?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`truncate ${strong ? "font-medium text-foreground" : ""}`} title={value ?? ""}>
        {value || "—"}
      </dd>
    </div>
  );
}
